"use client"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getStatuses, createStatus, updateStatus, deleteStatus } from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetStatuses = () => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["statuses"],
    queryFn: () => getStatuses(),
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useCreateStatus = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: (data) => createStatus(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["statuses"] });
      
      // Show success notification
      showNotification('success', 'Status created successfully!');
      
      // Navigate immediately without setTimeout
      navigate('/inventory-parameters?tab=status');
    },
    onError: (error) => {
      console.error("Error creating status:", error);
      
      // Show error notification
      let errorMessage = 'Error creating status. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};

export const useUpdateStatus = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => updateStatus(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["statuses"] });
      
      // Show success notification
      showNotification('success', 'Status updated successfully!');
      
      // Navigate immediately without setTimeout
      navigate('/inventory-parameters?tab=status');
    },
    onError: (error) => {
      console.error("Error updating status:", error);
      
      // Show error notification
      let errorMessage = 'Error updating status. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};

export const useDeleteStatus = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: (id) => deleteStatus(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["statuses"] });
      
      // Show success notification
      showNotification('success', 'Status deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting status:", error);
      
      // Show error notification
      let errorMessage = 'Error deleting status. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};